import { useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

export function getApiBaseUrl() {
  return process.env.REACT_APP_API_URL || '';
}

/**
 * Hook that returns API helpers with auth headers and base URL applied.
 * Use in components that need to call the backend (App, HistoryPage, AdminSettings, etc.).
 */
export function useApiClient() {
  const auth = useAuth();

  const apiFetch = useCallback(
    async (path, options = {}) => {
      const getHeaders = auth?.getAuthHeaders ?? (() => ({ 'Content-Type': 'application/json' }));
      const base = getApiBaseUrl();
      const url = path.startsWith('http') ? path : base ? `${base}${path}` : path;
      const headers = { ...getHeaders(), ...(options.headers || {}) };
      const { timeout, ...rest } = options;
      let signal = rest.signal;
      let abortId;
      if (timeout && !signal) {
        const controller = new AbortController();
        signal = controller.signal;
        abortId = setTimeout(() => controller.abort(), timeout);
      }
      try {
        const res = await fetch(url, { ...rest, headers, signal });
        if (abortId) clearTimeout(abortId);
        return res;
      } catch (e) {
        if (abortId) clearTimeout(abortId);
        throw e;
      }
    },
    [auth]
  );

  const apiGet = useCallback(
    async (path, options = {}) => {
      const res = await apiFetch(path, { ...options, method: 'GET' });
      if (!res.ok) {
        const err = new Error(res.statusText || `HTTP ${res.status}`);
        err.response = res;
        err.status = res.status;
        try {
          err.data = await res.json();
        } catch {
          err.data = null;
        }
        throw err;
      }
      return res.json();
    },
    [apiFetch]
  );

  return { apiGet, apiFetch, getApiBaseUrl };
}
